import React from 'react';
import SectionShell from '../SectionShell';
import Reveal from '../Reveal';
import FlickerNumber from '../FlickerNumber';
import { useT } from '../../i18n/LanguageContext';

/**
 * Side-by-side comparison with flicker counters:
 *   US-Dollar -88.89%  vs  Diamonds +1337.65%   (1960 → 2025)
 *
 * Each panel carries a decorative SVG (dollar bill for USD, brilliant-cut
 * diamond for Diamonds) that visually reinforces the story.
 */

// --- Decorative SVGs -------------------------------------------------------

function DollarBillSvg() {
  return (
    <svg
      viewBox="0 0 220 100"
      className="wert-bill"
      aria-hidden="true"
      preserveAspectRatio="xMidYMid meet"
    >
      {/* Outer rectangle — the bill body */}
      <rect x="4" y="4" width="212" height="92" rx="4" ry="4"
            fill="none" stroke="currentColor" strokeWidth="1.2" opacity="0.7" />
      {/* Inner decorative frame */}
      <rect x="12" y="12" width="196" height="76" rx="2" ry="2"
            fill="none" stroke="currentColor" strokeWidth="0.6" opacity="0.55" />
      {/* Central portrait oval */}
      <ellipse cx="110" cy="50" rx="26" ry="22"
               fill="none" stroke="currentColor" strokeWidth="0.9" opacity="0.65" />
      {/* Big $ in center */}
      <text x="110" y="60" textAnchor="middle"
            fontFamily="Manrope, serif" fontSize="26" fontWeight="500"
            fill="currentColor" opacity="0.85">$</text>
      {/* Corner denominations */}
      <text x="24" y="30" textAnchor="start"
            fontFamily="Manrope, sans-serif" fontSize="11" fontWeight="500"
            fill="currentColor" opacity="0.7">1</text>
      <text x="196" y="82" textAnchor="end"
            fontFamily="Manrope, sans-serif" fontSize="11" fontWeight="500"
            fill="currentColor" opacity="0.7">1</text>
      {/* Detail ticks — engraving vibe */}
      <g opacity="0.35" stroke="currentColor" strokeWidth="0.5">
        <line x1="24" y1="70" x2="72" y2="70" />
        <line x1="24" y1="74" x2="60" y2="74" />
        <line x1="148" y1="26" x2="196" y2="26" />
        <line x1="160" y1="30" x2="196" y2="30" />
      </g>
      {/* Downward-arrow decoration to hint at falling value */}
      <g opacity="0.9" stroke="currentColor" strokeWidth="1.4"
         strokeLinecap="round" strokeLinejoin="round" fill="none"
         className="wert-bill-arrow">
        <path d="M40 82 L40 92" />
        <path d="M35 87 L40 92 L45 87" />
      </g>
    </svg>
  );
}

function DiamondSvg() {
  return (
    <svg
      viewBox="0 0 96 96"
      className="wert-diamond"
      aria-hidden="true"
      preserveAspectRatio="xMidYMid meet"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.4"
      strokeLinejoin="round"
      strokeLinecap="round"
    >
      <path d="M75 15 21 15 3 38 48 83 48 83 93 38ZM30.69 40 43.8 70.32 13.48 40ZM61 40 48 70 35 40ZM34.71 36 39.32 21 56.68 21 61.29 36ZM65.31 40 82.51 40 52.22 70.29ZM83.82 36 65.48 36 60.86 21 72.08 21ZM23.92 21 35.14 21 30.52 36 12.18 36Z" />
    </svg>
  );
}

// --- Section ---------------------------------------------------------------

export default function SectionWertentwicklung() {
  const { t, lang } = useT();
  const SRC = {
    de: {
      cpiNote: 'Kaufkraftverlust nach US-BLS Consumer Price Index',
      gradeNote: 'Investment-Grade 1\u00a0ct D-IF · Quelle: Rapaport / IDEX',
      sourcesTitle: 'Quellen · Zum Nachprüfen',
      usdLabel: 'US-Dollar Kaufkraft:',
      diamondLabel: 'Diamant 1\u00a0ct D-IF:',
      asOf: 'Stichtag: Januar 2025. Alle Werte in USD, nominal (nicht inflationsbereinigt für Diamant-Reihe).',
    },
    en: {
      cpiNote: 'Loss of purchasing power per US-BLS Consumer Price Index',
      gradeNote: 'Investment grade 1\u00a0ct D-IF · Source: Rapaport / IDEX',
      sourcesTitle: 'Sources · For verification',
      usdLabel: 'US dollar purchasing power:',
      diamondLabel: 'Diamond 1\u00a0ct D-IF:',
      asOf: 'As of: January 2025. All values in USD, nominal (diamond series not adjusted for inflation).',
    },
    hu: {
      cpiNote: 'Vásárlóerő-vesztés a US-BLS fogyasztói árindex alapján',
      gradeNote: 'Befektetési minőség 1\u00a0ct D-IF · Forrás: Rapaport / IDEX',
      sourcesTitle: 'Források · Ellenőrzéshez',
      usdLabel: 'USA-dollár vásárlóerő:',
      diamondLabel: 'Gyémánt 1\u00a0ct D-IF:',
      asOf: 'Fordulónap: 2025. január. Minden érték USD-ben, nominálisan (a gyémánt-idősor inflációval nem korrigált).',
    },
    tr: {
      cpiNote: 'US-BLS tüketici fiyat endeksine göre satın alma gücü kaybı',
      gradeNote: 'Yatırım kalitesi 1\u00a0ct D-IF · Kaynak: Rapaport / IDEX',
      sourcesTitle: 'Kaynaklar · Doğrulama için',
      usdLabel: 'ABD doları satın alma gücü:',
      diamondLabel: 'Pırlanta 1\u00a0ct D-IF:',
      asOf: 'Tarih: Ocak 2025. Tüm değerler USD cinsinden, nominal (pırlanta serisi enflasyona göre düzeltilmemiştir).',
    },
  };
  const S = SRC[lang] || SRC.de;
  return (
    <SectionShell id="wertentwicklung" data-section-label="Wertentwicklung">
      <div>
        <Reveal>
          <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl tracking-tight leading-[1.05] mb-24 md:mb-32 text-white">
            {t.wertentwicklung.overline}
          </h2>
        </Reveal>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-10 md:gap-16">
          <Reveal>
            <div className="wert-panel wert-panel--usd hairline rounded-sm p-10 md:p-12 bg-white/[0.015] h-full relative overflow-hidden" data-testid="chart-usd">
              <div className="wert-visual wert-visual--usd" aria-hidden="true" style={{ color: '#E67373' }}>
                <DollarBillSvg />
              </div>
              <div className="relative">
                <div className="overline mb-6">{t.wertentwicklung.usd}</div>
                <div
                  className="font-display text-6xl md:text-7xl lg:text-8xl tracking-tighter mb-4 leading-none"
                  style={{ color: '#E67373', textShadow: '0 0 24px rgba(230, 115, 115, 0.28)' }}
                >
                  <FlickerNumber value={-49} decimals={0} duration={2200} suffix="%" />
                </div>
                <div className="overline text-white/40 mb-3">{t.wertentwicklung.note}</div>
                <div className="text-xs text-white/35 mb-8 leading-relaxed max-w-[34ch]">
                  {S.cpiNote}
                </div>
                <div className="relative h-2 bg-white/5 rounded-full overflow-hidden">
                  <div
                    className="absolute top-0 right-0 h-full"
                    style={{
                      width: '6%',
                      background: 'linear-gradient(90deg, transparent, #E67373)',
                      boxShadow: '0 0 16px rgba(230, 115, 115, 0.5)',
                    }}
                  />
                </div>
              </div>
            </div>
          </Reveal>

          <Reveal delay={150}>
            <div
              className="wert-panel wert-panel--diamond hairline rounded-sm p-10 md:p-12 h-full relative overflow-hidden"
              data-testid="chart-diamond"
              style={{
                background:
                  'linear-gradient(135deg, rgba(127, 207, 159, 0.05), rgba(255,255,255,0.02))',
              }}
            >
              <div className="wert-visual wert-visual--diamond" aria-hidden="true" style={{ color: '#7FCF9F' }}>
                <DiamondSvg />
              </div>
              <div className="relative">
                <div className="overline mb-6 text-white/70">{t.wertentwicklung.diamond}</div>
                <div
                  className="font-display text-6xl md:text-7xl lg:text-8xl tracking-tighter mb-4 leading-none"
                  style={{ color: '#7FCF9F', textShadow: '0 0 24px rgba(127, 207, 159, 0.32)' }}
                >
                  <FlickerNumber value={185} decimals={0} duration={2200} prefix="+" suffix="%" />
                </div>
                <div className="overline text-white/40 mb-3">{t.wertentwicklung.note}</div>
                <div className="text-xs text-white/35 mb-8 leading-relaxed max-w-[36ch]">
                  {S.gradeNote}
                </div>
                <div className="relative h-2 bg-white/5 rounded-full overflow-hidden">
                  <div
                    className="absolute top-0 left-0 h-full"
                    style={{
                      width: '90%',
                      background: 'linear-gradient(90deg, rgba(127, 207, 159, 0.4), #7FCF9F)',
                      boxShadow: '0 0 16px rgba(127, 207, 159, 0.45)',
                    }}
                  />
                </div>
              </div>
            </div>
          </Reveal>
        </div>

        {/* Sources footer — überprüfbare Quellenangaben mit URLs */}
        <Reveal delay={300}>
          <div className="mt-12 pt-6 border-t border-white/8 flex flex-col gap-2 text-xs text-white/45 leading-relaxed" data-testid="wert-sources">
            <div className="uppercase tracking-[0.28em] text-white/35 mb-1" style={{ fontSize: '10px' }}>
              {S.sourcesTitle}
            </div>
            <div>
              <span className="text-white/60">{S.usdLabel}</span>{' '}
              U.S. Bureau of Labor Statistics — CPI Inflation Calculator ·{' '}
              <a href="https://www.bls.gov/data/inflation_calculator.htm" target="_blank" rel="noopener noreferrer" className="underline hover:text-white/80 break-all">
                bls.gov/data/inflation_calculator.htm
              </a>
            </div>
            <div>
              <span className="text-white/60">{S.diamondLabel}</span>{' '}
              Rapaport Diamond Price Index (RAPI) / IDEX Online Diamond Price Index ·{' '}
              <a href="https://rapaport.com/diamond-price-index/" target="_blank" rel="noopener noreferrer" className="underline hover:text-white/80 break-all">
                rapaport.com/diamond-price-index
              </a>
              {' · '}
              <a href="https://www.idexonline.com/diamond_prices_index" target="_blank" rel="noopener noreferrer" className="underline hover:text-white/80 break-all">
                idexonline.com/diamond_prices_index
              </a>
            </div>
            <div className="text-white/30 italic mt-1">
              {S.asOf}
            </div>
          </div>
        </Reveal>
      </div>
    </SectionShell>
  );
}
